/**
 * PremiumPaywall - Modal card for Premium upgrade gate
 *
 * Purpose: Upgrade prompt for gated features
 * Layout: Frosted hero + gold lock badge + feature list
 * Animation: Staggered fade-in (label → title → subtitle → features → price/CTA)
 */

import { useState, useEffect } from 'react';
import { HeroImage } from './HeroImage';
import { BRAND, BRAND_IMAGES } from '@/styles/tokens';

interface PremiumPaywallProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade?: () => void;
}

const useFadeIn = (delay = 0) => {
  const [mounted, setMounted] = useState(false);
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    setTimeout(() => setMounted(true), 100 + delay);
  }, [delay]);

  return {
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(15px)',
    transition: prefersReducedMotion
      ? 'none'
      : 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  };
};

const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M6 8V6C6 3.79086 7.79086 2 10 2C12.2091 2 14 3.79086 14 6V8M5 8H15C15.5523 8 16 8.44772 16 9V16C16 16.5523 15.5523 17 15 17H5C4.44772 17 4 16.5523 4 16V9C4 8.44772 4.44772 8 5 8Z"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="8" cy="8" r="8" fill={BRAND.tealLight} />
    <path
      d="M5 8.5L7 10.5L11 6.5"
      stroke={BRAND.teal}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function PremiumPaywall({ isOpen, onClose, onUpgrade }: PremiumPaywallProps) {
  const labelStyle = useFadeIn(400);
  const titleStyle = useFadeIn(500);
  const subtitleStyle = useFadeIn(600);
  const featuresStyle = useFadeIn(650);
  const priceCtaStyle = useFadeIn(750);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
        padding: '24px',
      }}
      onClick={onClose}
    >
      {/* Modal card */}
      <div
        style={{
          position: 'relative',
          maxWidth: '380px',
          width: '100%',
          backgroundColor: BRAND.white,
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.20)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Frosted hero */}
        <HeroImage
          src={BRAND_IMAGES.complete}
          height={155}
          frost
          fadeToWhite
        />

        {/* Gold lock badge (overlapping hero by 22px) */}
        <div
          style={{
            position: 'absolute',
            top: '133px', // 155 - 22
            left: '50%',
            transform: 'translateX(-50%)',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${BRAND.goldWarm}, ${BRAND.gold})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(196, 162, 53, 0.3)',
          }}
        >
          <LockIcon />
        </div>

        {/* Content */}
        <div style={{ padding: '40px 24px 24px' }}>
          {/* PREMIUM label */}
          <div
            style={{
              ...labelStyle,
              fontFamily: BRAND.fontSans,
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '2.5px',
              textTransform: 'uppercase',
              color: BRAND.gold,
              textAlign: 'center',
              marginBottom: '12px',
            }}
          >
            PREMIUM
          </div>

          {/* Title */}
          <h2
            style={{
              ...titleStyle,
              fontFamily: BRAND.fontSerif,
              fontSize: '23px',
              fontWeight: 'bold',
              color: BRAND.ink,
              textAlign: 'center',
              marginBottom: '8px',
            }}
          >
            See the full picture
          </h2>

          {/* Subtitle */}
          <p
            style={{
              ...subtitleStyle,
              fontFamily: BRAND.fontSans,
              fontSize: '13px',
              lineHeight: 1.5,
              color: BRAND.inkLight,
              textAlign: 'center',
              marginBottom: '24px',
            }}
          >
            Unlock advanced insights and tools to price with complete confidence.
          </p>

          {/* Feature list */}
          <div style={{ ...featuresStyle, marginBottom: '24px' }}>
            {[
              'Unlimited forensic insights',
              'Material cost breakdown',
              'Market rate comparisons',
              'Quote generation tools',
            ].map((feature) => (
              <div
                key={feature}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 0',
                  fontFamily: BRAND.fontSans,
                  fontSize: '14px',
                  color: BRAND.ink,
                }}
              >
                <CheckIcon />
                {feature}
              </div>
            ))}
          </div>

          {/* Price */}
          <div
            style={{
              ...priceCtaStyle,
              textAlign: 'center',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                fontFamily: BRAND.fontSerif,
                fontSize: '34px',
                fontWeight: 'bold',
                color: BRAND.ink,
                lineHeight: 1,
                marginBottom: '4px',
              }}
            >
              $12.99
              <span
                style={{
                  fontSize: '13px',
                  fontFamily: BRAND.fontSans,
                  fontWeight: 500,
                  color: BRAND.inkLight,
                }}
              >
                /month
              </span>
            </div>
          </div>

          {/* CTA button */}
          <button
            onClick={onUpgrade}
            style={{
              ...priceCtaStyle,
              width: '100%',
              fontFamily: BRAND.fontSans,
              fontSize: '16px',
              fontWeight: 600,
              color: BRAND.white,
              background: `linear-gradient(135deg, ${BRAND.goldWarm}, ${BRAND.gold})`,
              padding: '16px 32px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              marginBottom: '12px',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(212, 168, 67, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Start 7-Day Free Trial
          </button>

          {/* Dismiss */}
          <button
            onClick={onClose}
            style={{
              width: '100%',
              fontFamily: BRAND.fontSans,
              fontSize: '13px',
              fontWeight: 500,
              color: BRAND.inkLight,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = BRAND.ink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = BRAND.inkLight;
            }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

export default PremiumPaywall;
